import { useCallback, useMemo } from 'react'
import { numberFormat } from 'shared/util'
import {
	Box, BoxProps, Alert, Stack,
	Typography, Chip,
	Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
	TableRowProps
} from "@mui/material"

import { ResearcherSummary } from '../types'

type CellValue = {
	code?: string | null
	text: string | number | null
	style?: string
	unit?: string
}
type ColumnDef = {
	key: string
	title: string
	align: 'left' | 'center' | 'right' | 'chips'
	hidden?: boolean
	expand?: boolean
	val: (researcher: ResearcherSummary) => CellValue | CellValue[] | null | undefined
}

const columns: ColumnDef[] = [
	{ key: 'name', title: '研究者名', align: 'left', val: researcher => ({ text: researcher.kojinName ?? null } as CellValue ) },
	{ key: 'affiliation', title: '所属施設', align: 'left', val: researcher => ({ text: researcher.affiliation ?? null } as CellValue ), expand: true },
	{ key: 'projectCount', title: '課題数', align: 'right', val: researcher => ({ text: researcher.projects.length, unit: '件' } as CellValue ) },
	{ key: 'primaryProjectCount', title: '責任者', align: 'right', val: researcher => ({ text: researcher.primaryProjectCount, unit: '件' } as CellValue ) },
	{ key: 'supportProjectCount', title: '分担者', align: 'right', val: researcher => ({ text: researcher.supportProjectCount, unit: '件' } as CellValue ) },
	{ key: 'cost', title: '研究費小計', align: 'right', val: researcher => ({ text: numberFormat(researcher.primaryCost + researcher.supportCost), unit: '円' } as CellValue ) },
]

type TableHeaderRowProps = {
	columns: ColumnDef[]
} & TableRowProps
const TableHeaderRow = ({
	columns,
	...others
}: TableHeaderRowProps) => {
	return (
		<TableRow {...others}>
			{columns.filter(column => !column.hidden).map((column) => (
				<TableCell key={column.key} style={{ width: 0, whiteSpace: 'nowrap' }}>{column.title}</TableCell>
			))}
			<TableCell/>
		</TableRow>
	)  
}

const DisableFieldRenderer = () => (
	<TableCell aria-disabled style={{ background: 'GhostWhite' }}></TableCell>
)

type TextFieldRendererProps = {
	text: string
	unit: string | null | undefined
	align: ColumnDef['align']
	expand?: boolean
}
const TextFieldRenderer = ({
	text,
	unit,
	align,
	expand
}: TextFieldRendererProps) => text === '' ? <DisableFieldRenderer/> : (
	<TableCell
		sx={(expand === true) ? {
			width: '100%',
			textAlign: align
		} : {
			whiteSpace: "nowrap",
			textAlign: align
		}}
	>
		{text}
		{unit ? (
			<Typography
				variant='body2'
				component='span'
				fontSize='80%'
				color='text.secondary'
				marginLeft={0.5}
			>
				{unit}
			</Typography>
		) : <></>}
	</TableCell>
)

type ArrayFieldRendererProps = {
	recordKey: string
	columnKey: string
	vals: CellValue[]
	expand?: boolean
}
const ArrayFieldRenderer = ({
	recordKey,
	columnKey,
	vals,
	expand
}: ArrayFieldRendererProps) => vals.length === 0 ? <DisableFieldRenderer/> : (
	<TableCell
		sx={(expand === true) ? {
			width: '100%'
		} : undefined}
	>
		<Stack direction="row" spacing={0.5}>
			{vals.map((v, j) => (
				<Chip
					key={`person_${recordKey}_${columnKey}_${j}`}
					size='small'
					label={(typeof v.text === 'number') ? numberFormat(v.text) : v.text}
				/>
			))}
		</Stack>
	</TableCell>
)

type ResearcherTableCellProps = {
	column: ColumnDef
	researcher: ResearcherSummary
}
const ResearcherTableCell = ({
	column,
	researcher
}: ResearcherTableCellProps) => {
	const value = useMemo(() => column.val(researcher), [column, researcher])
	return (value === null || value === undefined) ? (
		<DisableFieldRenderer/>
	) : (column.align === 'chips') ? (
		<ArrayFieldRenderer
			recordKey={researcher.kojinCode ?? researcher.kojinName}
			columnKey={column.key}
			vals={Array.isArray(value) ? value : [value]}
			expand={column.expand}
		/>
	) : (
		<TextFieldRenderer
			text={(Array.isArray(value) ? value : [value])
				.flatMap(v => v.text ? ((typeof v.text === 'number') ? numberFormat(v.text) : v.text) : [])
				.join(', ')}
			unit={Array.isArray(value) ? (value.length > 0 ? value[0].unit : null) : value.unit}
			align={column.align}
			expand={column.expand}
		/>
	)
}

type ResearcherTableRowProps = {
	index: number
	columns: ColumnDef[]
	researcher: ResearcherSummary
} & TableRowProps
const ResearcherTableRow = ({
	columns,
	researcher,
	...others
}: ResearcherTableRowProps) => (
	<TableRow {...others}>
		{columns.map(column => (
			<ResearcherTableCell
				key={`${researcher.kojinCode ?? researcher.kojinName}_${column.key}`}
				column={column}
				researcher={researcher}
			/>
		))}
	</TableRow>
)

type ResearcherTableProps = {
	researchers: ResearcherSummary[]
	selectedResearcher: string | null
	setSelectedResearcher: (personKey: string | null) => void
} & BoxProps
export const ResearcherTable = ({
	researchers,
	selectedResearcher,
	setSelectedResearcher,
	...others
}: ResearcherTableProps) => {
	const onClickedRow = useCallback(
		(researcher: ResearcherSummary) => {
			const key = researcher.kojinCode ?? researcher.kojinName
			setSelectedResearcher(selectedResearcher === key ? null : key)
		},
		[setSelectedResearcher, selectedResearcher]
	)
	const validColumns = useMemo(
		() => columns.filter(column => !column.hidden),
		[]
	)
	return (
		<Box {...others} position='relative'>
			{researchers.length === 0 ? (
				<Box padding={1}><Alert severity="info">対象が見つかりません.</Alert></Box>
			) : (
				<TableContainer sx={{ width: '100%', height: '100%' }}>
					<Table aria-label="doctors table" stickyHeader size="small">
						<TableHead>
							<TableHeaderRow columns={validColumns}/>
						</TableHead>
						<TableBody>
							{researchers.map((researcher, index) => (
								<ResearcherTableRow
									key={`person-record-${index}`}
									index={index}
									columns={validColumns}
									researcher={researcher}
									selected={selectedResearcher === (researcher.kojinCode ?? researcher.kojinName)}
									onClick={() => onClickedRow(researcher)}
									hover
									sx={{ cursor: 'pointer' }}
								/>
							))}
						</TableBody>
					</Table>
				</TableContainer>
			)}
		</Box>
	)
}